import { useParams, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { GeneratedContent } from "@/types/content";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { ContentGrid } from "@/components/content/ContentGrid";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Loader, Loader2, FileText, Search, PlusCircle, CheckCircle, AlertCircle, Filter, Plus, Check, Zap, Brain, X, ExternalLink } from "lucide-react";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";

const GeneratedContentPage = () => {
  const { id: projectId } = useParams();
  const { user } = useAuth();
  const { toast } = useToast();
  const location = useLocation();
  const [platform, setPlatform] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [isPosted, setIsPosted] = useState<boolean | null>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isFiltering, setIsFiltering] = useState(false);
  const [currentGenerationId, setCurrentGenerationId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [postingId, setPostingId] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const isGeneratingFromUrl = searchParams.get('generating') === 'true';
  const [isGenerating, setIsGenerating] = useState(isGeneratingFromUrl);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [currentTask, setCurrentTask] = useState("Analyzing content requirements...");
  
  // Add state for agent descriptions
  const [agentDescriptions, setAgentDescriptions] = useState({
    research: "Analyzing target audience patterns and content preferences...",
    writing: "Generating content structure based on topic analysis...",
    optimizing: "Refining content tone and style for engagement metrics..."
  });
  
  // Add state to track content being reset
  const [resettingIds, setResettingIds] = useState<string[]>([]);

  // Add state to track agent phase
  const [currentAgentPhase, setCurrentAgentPhase] = useState<'research' | 'writing' | 'optimizing'>('research');
  
  // Add state to track newly generated content
  const [newlyGeneratedIds, setNewlyGeneratedIds] = useState<string[]>([]);
  const [highlightedContentId, setHighlightedContentId] = useState<string | null>(null);
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});
  // Add ref for debouncing multiple content updates
  const newCompletedUpdateTimeout = useRef<NodeJS.Timeout | null>(null);
  
  // Add ref to track initial render
  const initialRenderRef = useRef(true);
  
  // Add ref to track if a successful generation has occurred during current session
  const hasSuccessfulGenerationRef = useRef(false);

  // Create refs to track intervals and timeouts
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const completionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const dismissTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Add a ref to track the last time the recovery mechanism was used
  const lastRecoveryTimestampRef = useRef(0);

  // Add state to track which content IDs we are watching for completion
  const [watchingIds, setWatchingIds] = useState<string[]>([]);

  // Add state to track when generation started
  const [generationStartTime, setGenerationStartTime] = useState<number | null>(null);

  // Define a helper function to safely access nested properties
  const safeGet = <T, K extends keyof T>(obj: T | undefined | null, key: K): T[K] | undefined => {
    return obj ? obj[key] : undefined;
  };

  const { data: contents = [], isLoading, error } = useQuery({
    queryKey: ['generated-content', projectId],
    queryFn: async () => {
      console.log('Fetching all content for project:', projectId);

      if (!projectId || !user?.id) {
        console.log('Missing projectId or userId');
        return [];
      }

      // Define allowed platforms
      const allowedPlatforms = ['blog', 'twitter', 'linkedin', 'video'];

      const { data: fetchedData, error } = await supabase
        .from('generated_content')
        .select()
        .eq('project_id', projectId)
        .eq('user_id', user?.id)
        .in('platform', allowedPlatforms)
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('Error fetching content:', error);
        throw error;
      }
      
      console.log('Fetched content:', fetchedData);
      return fetchedData as GeneratedContent[];
    },
    enabled: !!projectId && !!user?.id
  });

  // Check if any content is still pending
  const hasPendingContent = useMemo(() => {
    // Ensure contents is an array and check if any items are pending
    return Array.isArray(contents) && contents.some(content => content.status === 'pending');
  }, [contents]);
  
  // Check if there are no more pending items (for auto-completion)
  const noPendingContent = useMemo(() => {
    return !hasPendingContent;
  }, [hasPendingContent]);
  
  // Check if there are newly completed items (for auto-completion)
  const hasNewlyCompleted = useMemo(() => {
    return contents.some(c => 
      c.status === 'completed' && 
      !newlyGeneratedIds.includes(c.id)
    );
  }, [contents, newlyGeneratedIds]);
  
  // Add a function to reset stuck pending content
  const resetStuckPendingContent = useCallback(async () => {
    const pendingContents = contents?.filter(c => c.status === 'pending') || [];
    if (pendingContents.length === 0) return;
    
    // Check if any pending content is older than 10 minutes (stuck)
    const now = new Date();
    const stuckContents = pendingContents.filter(c => {
      const createdAt = new Date(c.created_at || '');
      const diffInMinutes = (now.getTime() - createdAt.getTime()) / (1000 * 60);
      return diffInMinutes > 10; // Consider content stuck if pending for more than 10 minutes
    });
    
    if (stuckContents.length === 0) return;
    
    // Reset the stuck content status to 'error'
    setResettingIds(stuckContents.map(c => c.id));
    
    // Collect IDs of successfully reset content
    const resetContentIds: string[] = [];
    
    for (const content of stuckContents) {
      try {
        await supabase
          .from('generated_content')
          .update({
            status: 'error',
            error_message: 'Generation timeout - process took too long'
          })
          .eq('id', content.id);
        
        // Add to list of successfully reset IDs
        resetContentIds.push(content.id);
        
        queryClient.invalidateQueries({ queryKey: ['generated-content', projectId] });
      } catch (error) {
        console.error('Error resetting stuck content:', error);
      }
    }
    
    setResettingIds([]);
    
    // Mark these reset content items as processed to prevent duplicate error toasts
    if (resetContentIds.length > 0) {
      setNewlyGeneratedIds(prev => [...prev, ...resetContentIds]);
      
      // Only show toast if there's NO successful generation yet
      // This prevents showing errors during mixed success/failure cases
      if (!hasSuccessfulGenerationRef.current) {
        toast({
          title: "Generation timeout detected",
          description: "Some content generation processes took too long and have been cancelled.",
          variant: "destructive",
        });
      }
    }
  }, [contents, projectId, queryClient, supabase, toast]);

  // Check for stuck pending content on load and periodically
  useEffect(() => {
    // Check immediately on load
    resetStuckPendingContent();
    
    // Check periodically every 30 seconds
    const interval = setInterval(resetStuckPendingContent, 30000);
    
    return () => clearInterval(interval);
  }, [resetStuckPendingContent]);
  
  // Replace our recent direct watcher with a more precise version
  useEffect(() => {
    // Only run if loader is showing and we have a start time
    if (!isGenerating || !generationStartTime) return;
    
    // Look for completed content that was created AFTER generation started
    const newlyCompletedContent = contents.some(content => {
      // Check if content has a status of completed
      if (content.status !== 'completed') return false;
      
      // Check if it was created after generation started
      const contentCreatedAt = new Date(content.created_at || 0).getTime();
      return contentCreatedAt > generationStartTime;
    });
    
    // If we found any newly completed content created after generation started, hide the loader
    if (newlyCompletedContent) {
      console.log('New content detected, hiding loader');
      setIsGenerating(false);
      setGenerationStartTime(null); // Reset the generation start time
      
      // Remove the generating=true URL param if it exists
      if (searchParams.has('generating')) {
        const updatedParams = new URLSearchParams(searchParams.toString());
        updatedParams.delete('generating');
        window.history.replaceState({}, '', `${window.location.pathname}${updatedParams.toString() ? '?' + updatedParams.toString() : ''}`);
      }
    }
  }, [contents, isGenerating, generationStartTime, searchParams]);

  // Update the effect that initializes generation to set the start time
  useEffect(() => {
    // Only run on initial load or when projectId changes
    if (!projectId) return;

    // Check if we should start the loader
    const startedFromUrl = isGeneratingFromUrl;
    const initialPendingItems = contents.filter(c => c.status === 'pending');

    if (startedFromUrl || initialPendingItems.length > 0) {
      setIsGenerating(true);
      setWatchingIds(initialPendingItems.map(c => c.id));
      // Set the generation start time to current timestamp
      setGenerationStartTime(Date.now());
      // Optionally reset progress bar/animation state here
      setLoadingProgress(0);
      setCurrentTask("Analyzing content requirements...");
      setCurrentAgentPhase('research');
    } else {
      setIsGenerating(false);
      setWatchingIds([]);
      setGenerationStartTime(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Track pending content IDs to detect when they complete
  useEffect(() => {
    // Only run this logic if we're actively generating or tracking pending content
    if (!isGenerating && watchingIds.length === 0) return;
    
    const currentPendingIds = contents
      .filter(c => c.status === 'pending')
      .map(c => c.id);
    
    // Store new pending IDs when we start generating content
    if (isGenerating && currentPendingIds.length > 0) {
      setWatchingIds(prev => {
        // Only add IDs that we're not already tracking
        const newIds = currentPendingIds.filter(id => !prev.includes(id));
        return [...prev, ...newIds];
      });
    }
    
    // If we had pending content that's now completed or failed, highlight it
    if (watchingIds.length > 0) {
      // Find newly completed/errored content (ones that were pending and are now completed/error)
      const newCompletedOrErrored = contents
        .filter(c => 
          watchingIds.includes(c.id) && 
          (c.status === 'completed' || c.status === 'error') &&
          !newlyGeneratedIds.includes(c.id) // Only include ones we haven't highlighted yet
        )
        .sort((a, b) => new Date(b.created_at || '').getTime() - new Date(a.created_at || '').getTime());
      
      if (newCompletedOrErrored.length > 0) {
        // Use a single update for multiple content items completing at once
        if (newCompletedUpdateTimeout.current) {
          clearTimeout(newCompletedUpdateTimeout.current);
        }
        
        // Function to ensure a platform section is expanded
        const ensurePlatformExpanded = (platformId: string) => {
          if (collapsedSections[platformId]) {
            setCollapsedSections(prev => ({
              ...prev,
              [platformId]: false
            }));
            return true; // Section needed expansion
          }
          return false; // Section was already expanded
        };
        
        // Debounce updates to prevent flickering with multiple items
        newCompletedUpdateTimeout.current = setTimeout(() => {
          // Only highlight completed (not error) content
          const firstCompleted = newCompletedOrErrored.find(c => c.status === 'completed');
          if (firstCompleted) {
            // Mark that we've had at least one successful generation
            hasSuccessfulGenerationRef.current = true;
            
            // First expand section if needed, then set highlighted ID after a short delay
            if (firstCompleted.platform) {
              const wasExpanded = ensurePlatformExpanded(firstCompleted.platform);
              
              // If section needed expansion, wait a bit before scrolling to give the DOM time to update
              if (wasExpanded) {
                setTimeout(() => {
                  setHighlightedContentId(firstCompleted.id);
                }, 300);
              } else {
                // Section was already expanded, so we can highlight immediately
                setHighlightedContentId(firstCompleted.id);
              }
            } else {
              setHighlightedContentId(firstCompleted.id);
            }
            
            // Show toast notification with link to new content
            toast({
              title: `New ${firstCompleted.platform} content created!`,
              description: (
                <div className="flex flex-col gap-2">
                  <div>{firstCompleted.title || 'Content'}</div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full mt-1 border-blue-200 hover:bg-blue-50"
                    onClick={() => {
                      // Clear highlight when directly navigating from toast
                      setHighlightedContentId(null);
                      navigate(`/projects/${projectId}/content/${firstCompleted.id}/edit`);
                    }}
                  >
                    View Content
                  </Button>
                </div>
              ),
              duration: 8000, // Show for 8 seconds
            });
            
            // Hide loader as soon as we have a successful completion
            if (isGenerating) {
              setIsGenerating(false);
              // Remove the generating=true URL param if it exists
              if (searchParams.has('generating')) {
                const updatedParams = new URLSearchParams(searchParams.toString());
                updatedParams.delete('generating');
                window.history.replaceState({}, '', `${window.location.pathname}${updatedParams.toString() ? '?' + updatedParams.toString() : ''}`);
              }
            }
          }
          
          // Check if there are any error items in the batch AND no successful items
          // Only show error toast if everything failed AND we haven't had any successful generations
          const erroredItems = newCompletedOrErrored.filter(c => c.status === 'error');
          const completedItems = newCompletedOrErrored.filter(c => c.status === 'completed');
          
          if (erroredItems.length > 0 && completedItems.length === 0 && !hasSuccessfulGenerationRef.current) {
            // Show a single toast for all errors in this batch, but ONLY if no success
            toast({
              title: "Content generation issue",
              description: "Some content could not be generated. Please try again or check your settings.",
              variant: "destructive",
            });
            
            // Handle case where all items error out by hiding the loader
            // If all watched items are now processed (all errored) and no successful items, hide loader
            const remainingPending = contents.filter(c => 
              watchingIds.includes(c.id) && 
              !newCompletedOrErrored.some(ec => ec.id === c.id) && 
              c.status === 'pending'
            );
            
            if (remainingPending.length === 0 && isGenerating) {
              setIsGenerating(false);
              // Remove the generating=true URL param if it exists
              if (searchParams.has('generating')) {
                const updatedParams = new URLSearchParams(searchParams.toString());
                updatedParams.delete('generating');
                window.history.replaceState({}, '', `${window.location.pathname}${updatedParams.toString() ? '?' + updatedParams.toString() : ''}`);
              }
            }
          }
          
          // Track all newly processed content
          setNewlyGeneratedIds(prev => {
            // Filter out any IDs that are already in the list
            const newIds = newCompletedOrErrored
              .map(c => c.id)
              .filter(id => !prev.includes(id));
            return [...prev, ...newIds];
          });
          
          // Update the pending IDs list to remove the ones that are now processed
          setWatchingIds(prev => 
            prev.filter(id => !newCompletedOrErrored.some(c => c.id === id))
          );
          
          newCompletedUpdateTimeout.current = null;
        }, 250); // 250ms debounce
      }
    }
  }, [contents, isGenerating, watchingIds, newlyGeneratedIds, collapsedSections, toast, navigate, projectId, searchParams]);

  // Scroll to highlighted content with retry logic
  useEffect(() => {
    if (highlightedContentId) {
      let retryCount = 0;
      const maxRetries = 10;
      
      // Function to attempt scrolling
      const attemptScroll = () => {
        const contentElement = document.getElementById(`content-card-${highlightedContentId}`);
        
        // If element exists, scroll to it
        if (contentElement) {
          // Check if the element is actually visible in DOM first
          const rect = contentElement.getBoundingClientRect();
          const isVisible = rect.height > 0 && rect.width > 0;
          
          if (!isVisible && retryCount < maxRetries) {
            // Element not properly rendered yet, retry
            retryCount++;
            setTimeout(attemptScroll, 200);
            return;
          }
          
          // Calculate optimal scroll position with offset
          // Use a percentage-based offset for better responsiveness
          const windowHeight = window.innerHeight;
          const headerOffset = Math.min(120, windowHeight * 0.15); // Either 120px or 15% of viewport height
          
          // Get fresh position after any section expansions
          const elementPosition = contentElement.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
          
          // Scroll with smoother animation
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
          
          // Add highlight animation after scrolling
          setTimeout(() => {
            contentElement.classList.add('animate-highlight');
          }, 500);
        }
        // Otherwise retry if we haven't hit max retries
        else if (retryCount < maxRetries) {
          retryCount++;
          // Linear backoff instead of exponential to avoid large gaps
          const retryDelay = 300 + (retryCount * 100);
          setTimeout(attemptScroll, retryDelay);
        }
      };
      
      // Delay to let DOM update - but not too long to avoid user scrolling before we do
      const initialTimeout = setTimeout(attemptScroll, 300);
      return () => clearTimeout(initialTimeout);
    }
  }, [highlightedContentId]);

  // Update descriptions periodically with predefined options
  useEffect(() => {
    let descriptionInterval: NodeJS.Timeout | undefined;
    
    if (isGenerating) {
      // Define descriptions for each phase - less frequent changes
      const researchDescriptions = [
                "Analyzing target audience patterns and content preferences...",
                "Collecting relevant industry trends and competitor insights...",
                "Processing topic data through semantic analysis framework...",
                "Building contextual knowledge graph for content strategy..."
      ];
      
      const writingDescriptions = [
                "Generating narrative structure based on research findings...",
                "Crafting compelling headlines and content hooks...",
                "Developing content body with key messaging priorities...",
                "Synthesizing information into coherent article format..."
      ];
      
      const optimizingDescriptions = [
                "Refining content tone and style for engagement metrics...",
                "Optimizing readability scores and SEO performance...",
                "Enhancing content with platform-specific formatting...",
                "Fine-tuning emotion and sentiment balance for target audience..."
      ];
      
      // Use a counter to cycle through descriptions
      let counter = 0;
      
      // Set initial descriptions based on current phase
      setAgentDescriptions({
        research: researchDescriptions[counter],
        writing: writingDescriptions[counter],
        optimizing: optimizingDescriptions[counter]
      });
      
      // Increase the interval time to reduce flicker
      descriptionInterval = setInterval(() => {
        counter = (counter + 1) % 4;
        
        // Only update the description for the current phase to minimize DOM updates
        if (currentAgentPhase === 'research') {
          setAgentDescriptions(prev => ({
            ...prev,
            research: researchDescriptions[counter]
          }));
        } else if (currentAgentPhase === 'writing') {
          setAgentDescriptions(prev => ({
            ...prev,
            writing: writingDescriptions[counter]
          }));
        } else if (currentAgentPhase === 'optimizing') {
          setAgentDescriptions(prev => ({
            ...prev,
            optimizing: optimizingDescriptions[counter]
          }));
        }
      }, 5000); // Increased from 3s to 5s to reduce flickering
    }

    return () => {
      if (descriptionInterval) {
        clearInterval(descriptionInterval);
      }
    };
  }, [isGenerating, currentAgentPhase]);

  const handleNavigateToEdit = useCallback((id: string) => {
    // Clear the highlight when a content card is clicked
    if (highlightedContentId === id) {
      setHighlightedContentId(null);
    }
    navigate(`/projects/${projectId}/content/${id}/edit`);
  }, [navigate, projectId, highlightedContentId, setHighlightedContentId]);

  const handleDelete = useCallback(async (id: string) => {
    setDeletingId(id);
    try {
      const { error } = await supabase
        .from('generated_content')
        .delete()
        .eq('id', id);

      if (error) throw error;

      queryClient.setQueryData(['generated-content', projectId], (old: GeneratedContent[] | undefined) => {
        if (!old) return [];
        return old.filter(c => c.id !== id);
      });

      toast({
        title: "Content deleted",
        description: "The content has been permanently deleted",
      });
    } catch (error: any) {
      console.error('Error deleting content:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete content",
        variant: "destructive",
      });
    } finally {
      setDeletingId(null);
    }
  }, [queryClient, toast, projectId]);

  const handlePost = useCallback(async (id: string) => {
    setPostingId(id);
    try {
      // Find the current content to determine its posted status
      const currentContent = contents?.find(c => c.id === id);
      const newPostedStatus = currentContent?.is_posted ? false : true;
      
      const { error } = await supabase
        .from('generated_content')
        .update({ is_posted: newPostedStatus })
        .eq('id', id);

      if (error) throw error;

      queryClient.setQueryData(['generated-content', projectId], (old: GeneratedContent[] | undefined) => {
        if (!old) return [];
        return old.map(c => c.id === id ? { ...c, is_posted: newPostedStatus } : c);
      });

      toast({
        title: newPostedStatus ? "Content posted" : "Post status removed",
        description: newPostedStatus 
          ? "The content has been marked as posted" 
          : "The content has been unmarked as posted",
      });
    } catch (error: any) {
      console.error('Error updating post status:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update post status",
        variant: "destructive",
      });
    } finally {
      setPostingId(null);
    }
  }, [queryClient, toast, projectId, contents]);

  // Log the content count for debugging
  useEffect(() => {
    console.log('Total content count:', contents?.length);
  }, [contents]);

  // Apply filters
  useEffect(() => {
    setIsFiltering(true);
    const timer = setTimeout(() => {
      setIsFiltering(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, platform, status, isPosted, sortOrder]);

  // Deduplicate content with identical titles in the same platform
  const deduplicatedContents = useMemo(() => {
    if (!contents) return [];
    
    // First sort by created_at to ensure newest items are processed last (and kept)
    const sortedContent = [...contents].sort((a, b) => {
      const dateA = new Date(a.created_at || '').getTime();
      const dateB = new Date(b.created_at || '').getTime();
      return dateA - dateB; // Ascending order - oldest first
    });
    
    // Map to track content by platform and normalized title
    const uniqueContentMap = new Map();
    
    sortedContent.forEach(content => {
      if (!content.title || !content.platform) return;
      
      // Create a normalized version of the title (lowercase, trimmed)
      const normalizedTitle = content.title.toLowerCase().trim();
      const key = `${content.platform}:${normalizedTitle}`;
      
      // This will overwrite any previous entry with the same key
      // Since we sorted oldest first, newest will be kept
      uniqueContentMap.set(key, content);
    });
    
    // Convert map values back to array
    const deduplicated = Array.from(uniqueContentMap.values());
    
    // Log the deduplication results
    const duplicatesRemoved = contents.length - deduplicated.length;
    if (duplicatesRemoved > 0) {
      console.log(`Deduplication removed ${duplicatesRemoved} duplicate content items`);
    }
    
    return deduplicated;
  }, [contents]);

  // Reset tracking state if user navigates away and back
  useEffect(() => {
    return () => {
      // Make sure all timers are cleared on unmount
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
      if (completionTimeoutRef.current) clearTimeout(completionTimeoutRef.current);
      if (dismissTimeoutRef.current) clearTimeout(dismissTimeoutRef.current);
      if (newCompletedUpdateTimeout.current) clearTimeout(newCompletedUpdateTimeout.current);
      
      // Reset refs to null
      progressIntervalRef.current = null;
      completionTimeoutRef.current = null;
      dismissTimeoutRef.current = null;
      newCompletedUpdateTimeout.current = null;
      
      // Reset success tracker on unmount
      hasSuccessfulGenerationRef.current = false;
    };
  }, []);

  // Clear highlighted content when user navigates to a different project
  useEffect(() => {
    // When projectId changes, reset the highlight state
    setHighlightedContentId(null);
    setNewlyGeneratedIds([]);
    // Reset success tracker on project change
    hasSuccessfulGenerationRef.current = false;
  }, [projectId]);

  // Create filtered content based on state
  const filteredContent = useMemo(() => {
    if (!contents || contents.length === 0) return [];

    // Define allowed platform types
    const allowedPlatforms = ['blog', 'twitter', 'linkedin', 'video'];
    
    // Start with filtering by allowed platforms
    let filtered = [...contents].filter(content => 
      content.platform && allowedPlatforms.includes(content.platform)
    );

    // Filter by platform
    if (platform) {
      filtered = filtered.filter(content => content.platform === platform);
    }

    // Filter by status
    if (status) {
      filtered = filtered.filter(content => content.status === status);
    }

    // Filter by posted status
    if (isPosted !== null) {
      filtered = filtered.filter(content => content.is_posted === isPosted);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(content => {
        return (
          (content.title?.toLowerCase().includes(query)) ||
          (content.content?.toLowerCase().includes(query)) ||
          (content.platform?.toLowerCase().includes(query))
        );
      });
    }

    // Sort by created date
    filtered.sort((a, b) => {
      const dateA = new Date(a.created_at || '').getTime();
      const dateB = new Date(b.created_at || '').getTime();
      return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
    });

    return filtered;
  }, [contents, platform, status, isPosted, searchQuery, sortOrder]);

  // Ensure all components can access the highlighted content
  useEffect(() => {
    // If there's a highlighted card but it's not in the DOM yet,
    // no class will be added. This ensures the class gets applied
    // after component updates.
    if (highlightedContentId) {
      const applyHighlight = () => {
        const element = document.getElementById(`content-card-${highlightedContentId}`);
        if (element && !element.classList.contains('animate-highlight')) {
          element.classList.add('animate-highlight');
        }
      };
      
      // Try immediately and after a short delay
      applyHighlight();
      const timeout = setTimeout(applyHighlight, 100);
      return () => clearTimeout(timeout);
    }
  }, [highlightedContentId, filteredContent]);

  // Simulated progress bar and text animation interval
  useEffect(() => {
    if (!isGenerating) {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
      return;
    }

    // Define generation steps - static configuration
    const generationSteps = [
      { progress: 0, task: "Analyzing target audience patterns and content preferences...", phase: 'research' },
      { progress: 10, task: "Collecting relevant industry trends and competitor insights...", phase: 'research' },
      { progress: 20, task: "Processing topic data through semantic analysis framework...", phase: 'research' },
      { progress: 30, task: "Building contextual knowledge graph for content strategy...", phase: 'research' },
      { progress: 40, task: "Generating narrative structure based on research findings...", phase: 'writing' },
      { progress: 50, task: "Crafting compelling headlines and content hooks...", phase: 'writing' },
      { progress: 60, task: "Developing content body with key messaging priorities...", phase: 'writing' },
      { progress: 70, task: "Refining content tone and style for engagement metrics...", phase: 'optimizing' },
      { progress: 80, task: "Optimizing readability scores and SEO performance...", phase: 'optimizing' },
      { progress: 90, task: "Finalizing content for your selected platforms...", phase: 'optimizing' }
    ];

    let stepIndex = 0;
    setLoadingProgress(generationSteps[0].progress);
    setCurrentTask(generationSteps[0].task);
    setCurrentAgentPhase(generationSteps[0].phase as 'research' | 'writing' | 'optimizing');

    progressIntervalRef.current = setInterval(() => {
      if (stepIndex < generationSteps.length - 1) {
        stepIndex++;
        setLoadingProgress(generationSteps[stepIndex].progress);
        setCurrentTask(generationSteps[stepIndex].task);
        setCurrentAgentPhase(generationSteps[stepIndex].phase as 'research' | 'writing' | 'optimizing');
      }
    }, 4000);

    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    };
  }, [isGenerating]);

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center text-red-500">
          Error loading content: {error.message}
        </div>
      </div>
    );
  }

  return (
    <div className="container py-6 max-w-7xl">
      <div className="flex flex-col space-y-6">
        {/* Header with title and action buttons */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight gradient-text">
              AI Generated Content
            </h1>
            <p className="text-muted-foreground mt-1">
              Manage and edit your AI-generated content for various platforms and use cases
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => navigate(`/projects/${projectId}/topics`)}
            >
              <Plus className="h-4 w-4" />
              New Content
            </Button>
            <Button
              onClick={() => navigate(`/projects/${projectId}/topics`)}
              disabled={isGenerating}
              className="gap-1.5 bg-primary hover:bg-primary/90"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <PlusCircle className="h-4 w-4" />
                  Generate Content
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Main Content */}
        <div>
          {/* New Content Generation Loader */}
          {isGenerating && (
            <Card className="mb-6 border-zinc-200 dark:border-zinc-700 overflow-hidden">
              <CardContent className="p-6">
                <div className="space-y-6">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-full bg-blue-100 dark:bg-blue-900/30">
                        <Brain className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                      </div>
                      <h3 className="font-medium text-zinc-900 dark:text-white text-lg">
                        {contents?.filter(c => c.status === 'pending').length > 1 
                      ? `AI Cluster Processing ${contents?.filter(c => c.status === 'pending').length} Content Units`
                          : "AI Content Generation"
                        }
                      </h3>
                    </div>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => {
                        setIsGenerating(false);
                        setLoadingProgress(0);
                        setGenerationStartTime(null); // Reset the generation start time
                        // Remove the generating=true URL param if it exists
                        if (searchParams.has('generating')) {
                          const updatedParams = new URLSearchParams(searchParams.toString());
                          updatedParams.delete('generating');
                          window.history.replaceState({}, '', `${window.location.pathname}${updatedParams.toString() ? '?' + updatedParams.toString() : ''}`);
                        }
                      }}
                      className="border-zinc-200 dark:border-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-800"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="font-medium text-blue-700 dark:text-blue-400">
                        {currentAgentPhase === 'research' && 'Research Phase'}
                        {currentAgentPhase === 'writing' && 'Content Writing Phase'}
                        {currentAgentPhase === 'optimizing' && 'Optimization Phase'}
                      </span>
                      <span className="font-medium">{loadingProgress}%</span>
                    </div>
                    <div className="h-2 bg-blue-100 dark:bg-blue-900/30 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-blue-600 to-sky-500 rounded-full transition-all duration-500 ease-out"
                        style={{ width: `${loadingProgress}%` }}
                      ></div>
                    </div>
                    <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-2 font-mono">{currentTask}</p>
                    
                    <div className="mt-4 grid grid-cols-3 gap-3 pt-2">
                      {/* Research Phase */}
                      <div className={`p-3 rounded-lg border ${
                        currentAgentPhase === 'research' 
                          ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800' 
                          : 'bg-transparent border-zinc-200 dark:border-zinc-800'
                      }`}>
                        <div className="flex items-center gap-2">
                          <div className={`p-1.5 rounded-full ${
                            currentAgentPhase === 'research' ? 'bg-blue-100 dark:bg-blue-800/50' : 'bg-zinc-100 dark:bg-zinc-800'
                          }`}>
                            <Brain className={`h-3.5 w-3.5 ${
                              currentAgentPhase === 'research' ? 'text-blue-600 dark:text-blue-400' : 'text-zinc-500 dark:text-zinc-400'
                            }`} />
                          </div>
                          <span className={`text-xs font-medium ${
                            currentAgentPhase === 'research' ? 'text-blue-700 dark:text-blue-400' : 'text-zinc-500 dark:text-zinc-400'
                          }`}>
                            Research
                          </span>
                        </div>
                      </div>
                      
                      {/* Writing Phase */}
                      <div className={`p-3 rounded-lg border ${
                        currentAgentPhase === 'writing' 
                          ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800' 
                          : 'bg-transparent border-zinc-200 dark:border-zinc-800'
                      }`}>
                        <div className="flex items-center gap-2">
                          <div className={`p-1.5 rounded-full ${
                            currentAgentPhase === 'writing' ? 'bg-blue-100 dark:bg-blue-800/50' : 'bg-zinc-100 dark:bg-zinc-800'
                          }`}>
                            <FileText className={`h-3.5 w-3.5 ${
                              currentAgentPhase === 'writing' ? 'text-blue-600 dark:text-blue-400' : 'text-zinc-500 dark:text-zinc-400'
                            }`} />
                          </div>
                          <span className={`text-xs font-medium ${
                            currentAgentPhase === 'writing' ? 'text-blue-700 dark:text-blue-400' : 'text-zinc-500 dark:text-zinc-400'
                          }`}>
                            Writing
                          </span>
                        </div>
                      </div>
                      
                      {/* Optimizing Phase */}
                      <div className={`p-3 rounded-lg border ${
                        currentAgentPhase === 'optimizing' 
                          ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800' 
                          : 'bg-transparent border-zinc-200 dark:border-zinc-800'
                      }`}>
                        <div className="flex items-center gap-2">
                          <div className={`p-1.5 rounded-full ${
                            currentAgentPhase === 'optimizing' ? 'bg-blue-100 dark:bg-blue-800/50' : 'bg-zinc-100 dark:bg-zinc-800'
                          }`}>
                            <Zap className={`h-3.5 w-3.5 ${
                              currentAgentPhase === 'optimizing' ? 'text-blue-600 dark:text-blue-400' : 'text-zinc-500 dark:text-zinc-400'
                            }`} />
                          </div>
                          <span className={`text-xs font-medium ${
                            currentAgentPhase === 'optimizing' ? 'text-blue-700 dark:text-blue-400' : 'text-zinc-500 dark:text-zinc-400'
                          }`}>
                            Optimizing
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          
          <ContentGrid
            contents={filteredContent}
            onDelete={handleDelete}
            onPost={handlePost}
            deletingId={deletingId}
            postingId={postingId}
            onCardClick={handleNavigateToEdit}
            highlightedContentId={highlightedContentId}
            collapsedSections={collapsedSections}
            setCollapsedSections={setCollapsedSections}
          />
        </div>
      </div>
    </div>
  );
};

export default GeneratedContentPage;